import dayjs from 'dayjs';
import localization from 'localizations/es';

type Options = { [index: string]: string | number | undefined | null };
type Cache = { [index: string]: string | number };

const memo: Cache = {};

export const NBSP_SYMBOL = '\xa0';
export const NUMBER_DELIMETER = ',';
export const NUMBER_GROUP_SIZE = 3;
export const NUMBER_GROUP_DELIMITER = '.';

export function numberToLocale(
  number: number | undefined | null,
  precision: number = 2,
  trailingZero: boolean = true,
): string {
  if (number === undefined || number === null || Number.isNaN(number)) {
    return '';
  }

  const numberString = String(number);
  const isInteger = Number.isInteger(number);
  // Split number into integer and fractional parts, applying trailing zero padding if needed
  const getNumberParts = () => {
    if (isInteger) {
      return [numberString, '00'];
    }

    if (trailingZero) {
      return number.toFixed(precision).split(NUMBER_GROUP_DELIMITER);
    }

    return numberString.split(NUMBER_GROUP_DELIMITER);
  };

  const numberParts = getNumberParts();
  const isNegative = number < 0;
  // Strip leading minus sign before grouping so it can be re-applied at the end
  const integer = isNegative ? numberParts[0].substring(1) : numberParts[0];
  const numberOfIterations = Math.ceil(integer.length / NUMBER_GROUP_SIZE);
  let result = '';

  // Iterate right-to-left in chunks of NUMBER_GROUP_SIZE, inserting thousand separators
  for (let i = integer.length, j = 1; i > 0; i -= NUMBER_GROUP_SIZE, j++) {
    const append = result;
    const prepend = integer.substring(i - NUMBER_GROUP_SIZE, i);

    // Skip leading separator for the first (leftmost) group
    if (prepend.length < NUMBER_GROUP_SIZE || j === numberOfIterations) {
      result = `${prepend}${append}`;
    } else {
      result = `${NUMBER_GROUP_DELIMITER}${prepend}${append}`;
    }
  }

  const fraction = numberParts[1];
  if (precision)
    result += `${NUMBER_DELIMETER}${fraction.substring(0, precision)}`;

  if (isNegative) {
    result = `-${result}`;
  }

  return result;
}

export function numberWithSuffix(
  number: number,
  suffix: string,
  precision?: number,
): string {
  const prepend = numberToLocale(number, precision);

  return `${prepend}${NBSP_SYMBOL}${suffix}`;
}

export function numberToCurrency(number?: number, precision?: number): string {
  if (number === undefined) {
    return '';
  }

  const suffix = translate('common.currencySymbol');

  return numberWithSuffix(number, suffix, precision);
}

export function numberToPercent(number?: number, precision?: number): string {
  if (number === undefined) {
    return '';
  }

  const suffix = '%';

  return numberWithSuffix(number, suffix, precision);
}

export const DEFAULT_DATE_FORMAT = 'DD/MM/YYYY';

export function dateFormat(
  date: string | undefined,
  format: string = DEFAULT_DATE_FORMAT,
): string {
  if (date === undefined) {
    return '';
  }

  return dayjs(date).format(format);
}

// Escapes special regex characters so user-provided strings can be safely embedded in RegExp
function escapeRegExp(string: string) {
  return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function translate(
  key: string,
  options?: Options | null | undefined,
  messages: Object = localization,
): string {
  // Return cached result immediately to avoid repeated lookups
  if (memo[key]) {
    return String(memo[key]);
  }

  let optionsKey = '';

  if (options) {
    // Build a unique cache key that includes interpolation values
    optionsKey = `${key}${JSON.stringify(options)}`;

    if (memo[optionsKey]) {
      return String(memo[optionsKey]);
    }
  }

  // Normalize bracket notation (e.g. key[0]) to dot notation (key.0)
  const path: string = key.replace(/\[(\w+)\]/g, '.$1');
  const sanitizedPath: string = path.replace(/^\./, '');
  const arr: any[] = sanitizedPath.split(NUMBER_GROUP_DELIMITER);

  try {
    // Walk the localization object using the dot-separated path segments
    const message = arr.reduce(
      (accumulator, currentValue) => accumulator[currentValue],
      messages,
    );

    if (message) {
      if (options) {
        const objectKeys = Object.keys(options);
        // Replace each {placeholder} and resolve ICU-style plural rules
        const messageWithOptions = objectKeys.reduce(
          (accumulator, currentValue) => {
            const optionValue = options[currentValue] || '';
            // Numeric values use "=N" prefix in ICU plural syntax
            const prefix =
              typeof optionValue === 'number' ? `=${optionValue}` : optionValue;
            const PHRAZE_REGEXP = new RegExp(
              `${escapeRegExp(prefix)} \\[([^[\\]]+(?=]))\\]`,
            );

            // Fallback to "other" branch when no exact plural match is found
            const PHRAZE_FALLBACK_REGEXP = new RegExp(
              'other \\[([^[\\]]+(?=]))\\]',
            );
            let phraze = '';
            const SANITIZE_REGEXP = new RegExp(`{${currentValue}}`, 'g');
            const sanitizedMessage = accumulator.replace(
              SANITIZE_REGEXP,
              optionValue,
            );

            if (sanitizedMessage.match(PHRAZE_REGEXP)) {
              phraze = sanitizedMessage.match(PHRAZE_REGEXP)[1];
            } else if (sanitizedMessage.match(PHRAZE_FALLBACK_REGEXP)) {
              phraze = sanitizedMessage.match(PHRAZE_FALLBACK_REGEXP)[1];
            }

            // Replace # placeholder inside the matched plural branch with the actual number
            const phrazeWithNumber = phraze.replace(
              /#/,
              String(options[currentValue]),
            );
            const PLURALIZED_MESSAGE_REGEXP = new RegExp(
              `{${currentValue}, plural, .*?}`,
            );
            const pluralizedMessage = sanitizedMessage.replace(
              PLURALIZED_MESSAGE_REGEXP,
              phrazeWithNumber,
            );

            return pluralizedMessage;
          },
          message,
        );

        memo[optionsKey] = messageWithOptions;

        return messageWithOptions;
      }

      memo[key] = message;

      return message;
    }

    // Return the key path wrapped in braces as a fallback when no message is found
    memo[key] = `{${sanitizedPath}}`;

    return `{${sanitizedPath}}`;
  } catch (exception) {
    memo[key] = `{${sanitizedPath}}`;

    return `{${sanitizedPath}}`;
  }
}

export default translate;
